/**
 * Authenticated fetch wrapper.
 * Uses httpOnly cookie as primary auth (sent automatically with credentials: 'include').
 * Falls back to localStorage token in Authorization header for cross-origin dev scenarios.
 *
 * On 401: attempts silent token refresh via /api/auth/refresh, retries once.
 *
 * Usage:
 *   import { authFetch } from '../services/authFetch';
 *   const res = await authFetch('/api/some-endpoint');
 *   const res = await authFetch('/api/endpoint', { method: 'POST', body: JSON.stringify(data) });
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FETCH_TIMEOUT = 20000; // 20s global timeout for all API calls

let isRefreshing = false;
let refreshPromise = null;

function fetchWithTimeout(url, options = {}, timeout = FETCH_TIMEOUT) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeout);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(tid));
}

async function attemptRefresh() {
  if (isRefreshing) return refreshPromise;
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      }, 10000);
      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        return true;
      }
    } catch (e) {}
    return false;
  })();
  const result = await refreshPromise;
  isRefreshing = false;
  refreshPromise = null;
  return result;
}

export async function authFetch(path, options = {}) {
  const token = localStorage.getItem('token');
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const timeout = options._timeout || FETCH_TIMEOUT;

  const { _timeout, ...restOptions } = options;
  const config = {
    ...restOptions,
    credentials: 'include',
    headers: {
      ...restOptions.headers,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  };

  let response = await fetchWithTimeout(url, config, timeout);

  // On 401, try refresh and retry once
  if (response.status === 401) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      const newToken = localStorage.getItem('token');
      config.headers = {
        ...config.headers,
        ...(newToken ? { 'Authorization': `Bearer ${newToken}` } : {}),
      };
      response = await fetchWithTimeout(url, config);
    } else {
      // Refresh failed — session expired, force logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const path = window.location.pathname;
      if (!path.includes('/login') && !path.includes('/sales-call/') && !path.includes('/meeting/')) {
        window.location.href = '/login';
      }
    }
  }

  return response;
}

/**
 * Get the current auth token for manual header construction.
 * Prefer authFetch() over this when possible.
 */
export function getAuthToken() {
  return localStorage.getItem('token');
}
